// src/components/common/ProtectedRoute.jsx
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

// ── Map role → home route ──────────────────────────────────
export function getDashboardPath(role) {
  const map = {
    admin:    "/admin/dashboard",
    hr:       "/hr/dashboard",
    finance:  "/finance/dashboard",
    lead:     "/lead/dashboard",
    employee: "/employee/dashboard",
  };
  return map[role] ?? "/login";
}

// ── Full-screen auth loading screen ───────────────────────
function AuthLoader({ message = "Verifying your session…" }) {
  return (
    <div className="hrms-loading-screen" role="status" aria-live="polite">
      <div className="hrms-spinner" aria-hidden="true" />
      <p className="hrms-loading-msg">{message}</p>
    </div>
  );
}

// ── ProtectedRoute ─────────────────────────────────────────
// Props:
//   role  → string OR array of strings e.g. "admin" | ["admin","lead"]
//           If omitted, only authentication is required.
export default function ProtectedRoute({ children, role }) {
  const { currentUser, userProfile, loading } = useAuth();
  const location = useLocation();

  // ── STEP 1: Wait for Firebase Auth + Firestore to both resolve ──
  // This single guard prevents ANY redirect until we know for sure
  // whether the user is authenticated and what their role is.
  if (loading) {
    return <AuthLoader message="Loading your account…" />;
  }

  // ── STEP 2: Not logged in → go to login ─────────────────────────
  if (!currentUser) {
    // Preserve the intended URL so we can redirect back after login
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // ── STEP 3: Logged in but Firestore profile missing ─────────────
  // This happens if someone authenticates but no users/{uid} doc exists.
  if (!userProfile) {
    return (
      <div className="hrms-loading-screen">
        <div className="hrms-spinner" />
        <p className="hrms-loading-msg">Setting up your profile…</p>
        <p className="hrms-loading-sub">
          If this persists, ask your administrator to complete your account setup.
        </p>
      </div>
    );
  }

  // ── STEP 4: Role-based access check ─────────────────────────────
  if (role) {
    const allowedRoles = Array.isArray(role) ? role : [role];

    if (!allowedRoles.includes(userProfile.role)) {
      // User is authenticated but trying to access the wrong dashboard.
      // Silently redirect them to THEIR correct dashboard.
      return <Navigate to={getDashboardPath(userProfile.role)} replace />;
    }
  }

  // ── STEP 5: All checks passed → render the page ─────────────────
  return children;
}
